import React, { useEffect, useState, useCallback } from 'react';
import { Search } from 'lucide-react';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'];
const METHOD_LABELS: Record<string, string> = {
  CASH: 'Cash', BANK_TRANSFER: 'Bank Transfer', MOBILE_MONEY: 'Mobile Money', CARD: 'Card', OTHER: 'Other',
};
const LIMIT = 20;

export const AgentPayments: React.FC = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [trackingSearch, setTrackingSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (trackingSearch) filters.trackingNumber = trackingSearch;
      const res = await apiClient.listPayments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setPayments(d?.payments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch {} finally { setLoading(false); }
  }, [page, statusFilter, trackingSearch]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Payments</h1>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              placeholder="Search tracking number..."
              value={trackingSearch}
              onChange={e => { setTrackingSearch(e.target.value); setPage(0); }}
              style={{ paddingLeft: 32, width: '100%' }}
            />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
        </div>

        {loading ? <div className="loading-spinner">Loading payments...</div> : payments.length === 0 ? (
          <div className="empty-state"><h3>No payments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Shipment</th><th>Method</th><th>Amount</th><th>Status</th><th>Reference</th><th>Due Date</th><th>Paid At</th></tr>
                </thead>
                <tbody>
                  {payments.map((p: any) => (
                    <tr key={p.id}>
                      <td><code style={{ fontSize: 11 }}>{p.Shipment?.trackingNumber || p.shipment?.trackingNumber || p.shipmentId?.slice(0, 8)}</code></td>
                      <td>{METHOD_LABELS[p.paymentMethod] || p.paymentMethod}</td>
                      <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{Number(p.amount).toLocaleString()} {p.currency}</td>
                      <td><span className={`badge badge-${p.status?.toLowerCase()}`}>{p.status}</span></td>
                      <td style={{ fontSize: 12 }}>{p.transactionReference || '—'}</td>
                      <td style={{ fontSize: 12 }}>{p.dueDate ? new Date(p.dueDate).toLocaleDateString() : '—'}</td>
                      <td style={{ fontSize: 12 }}>{p.paidAt ? new Date(p.paidAt).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
